import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  Paper,
  Divider,
  Container,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  TextField,
  Alert,
  Skeleton
} from '@mui/material';
import {
  LineChart, Line, PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  Percent as PercentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ExpandLess,
  ExpandMore,
  HealthAndSafety as ValidateIcon,
  FileDownload as FileDownloadIcon,
  GetApp as GetAppIcon
} from '@mui/icons-material';
import { getConnections, getContractsKpis, getContractsForecast, getFinancialSummary, getFinancialByPartner, getFinancialByCategory, validateContracts, getMonthlyInvoicing, getMonthlyInvoicingContracts } from '../../api/api';
import { exportToExcel, exportToPDF, exportChartsToPDF } from '../../utils/exportUtils';

const COLORS_CUSTOMER = ['#2196F3', '#1976D2', '#1565C0'];
const COLORS_SUPPLIER = ['#FF6B6B', '#FF5252', '#FF1744'];

function KPICard({ icon: Icon, label, value, unit = '', bgColor = '#2196F3', trend = null }) {
  const showTrend = trend !== null && trend !== undefined;
  const isPositive = trend > 0;
  const isNegative = trend < 0;
  
  return (
    <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: `${bgColor}20`, borderLeft: `4px solid ${bgColor}` }}>
      <Box display="flex" justifyContent="center" mb={1}>
        <Icon sx={{ fontSize: 32, color: bgColor }} />
      </Box>
      <Typography variant="body2" color="textSecondary">{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 'bold', color: bgColor }}>
        {typeof value === 'number' ? value.toLocaleString('es-ES', { maximumFractionDigits: 2 }) : value}
        {unit && <span style={{ fontSize: '0.7em' }}> {unit}</span>}
      </Typography>
      {showTrend && (
        <Box display="flex" justifyContent="center" mt={1}>
          <Chip
            size="small"
            icon={isPositive ? <TrendingUpIcon /> : isNegative ? <TrendingDownIcon /> : null}
            label={`${isPositive ? '+' : ''}${trend}% vs ${new Date().getFullYear() - 1}`}
            sx={{
              backgroundColor: isPositive ? '#4CAF5020' : isNegative ? '#FF525220' : '#9E9E9E20',
              color: isPositive ? '#4CAF50' : isNegative ? '#FF5252' : '#9E9E9E',
              fontWeight: 'bold',
              fontSize: '0.75rem'
            }}
          />
        </Box>
      )}
    </Paper>
  );
}

function KPISkeleton() {
  return (
    <Paper sx={{ p: 2, textAlign: 'center' }}>
      <Box display="flex" justifyContent="center" mb={1}>
        <Skeleton variant="circular" width={40} height={40} />
      </Box>
      <Skeleton variant="text" width="60%" sx={{ mx: 'auto', mb: 1 }} />
      <Skeleton variant="text" width="80%" height={40} sx={{ mx: 'auto' }} />
      <Skeleton variant="rectangular" width="70%" height={24} sx={{ mx: 'auto', mt: 1, borderRadius: 3 }} />
    </Paper>
  );
}

function ChartSkeleton({ height = 300 }) {
  return (
    <Card>
      <CardContent>
        <Skeleton variant="text" width="40%" height={30} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={height} sx={{ borderRadius: 1 }} />
      </CardContent>
    </Card>
  );
}

function DashboardByConnection() {
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [kpis, setKpis] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [financialByPartner, setFinancialByPartner] = useState(null);
  const [financialByCategory, setFinancialByCategory] = useState(null);
  const [monthlyInvoicing, setMonthlyInvoicing] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedMonthType, setSelectedMonthType] = useState('customer');
  const [monthContracts, setMonthContracts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [expandedIssue, setExpandedIssue] = useState(null);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [showValidationRules, setShowValidationRules] = useState(false);

  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    try {
      const response = await getConnections();
      setConnections(response.data.data);
      if (response.data.data.length > 0) {
        setSelectedConnection(response.data.data[0].id);
      }
    } catch (error) {
      console.error('Error loading connections:', error);
    }
  };

  useEffect(() => {
    if (selectedConnection) {
      loadDashboardData();
    }
  }, [selectedConnection, selectedYear]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load all data in parallel with individual error handling
      const [kpisResult, forecastResult, financialResult, partnerResult, categoryResult, monthlyResult] = await Promise.allSettled([
        getContractsKpis(selectedConnection, selectedYear),
        getContractsForecast(selectedConnection),
        getFinancialSummary(selectedConnection, selectedYear),
        getFinancialByPartner(selectedConnection, selectedYear),
        getFinancialByCategory(selectedConnection, selectedYear),
        getMonthlyInvoicing(selectedConnection, selectedYear)
      ]);

      // Process each result with error handling
      if (kpisResult.status === 'fulfilled') {
        setKpis(kpisResult.value.data.data);
      } else {
        console.error('Error loading KPIs:', kpisResult.reason);
        setKpis(null);
      }

      if (forecastResult.status === 'fulfilled') {
        setForecast(forecastResult.value.data.data);
      } else {
        console.error('Error loading Forecast:', forecastResult.reason);
        setForecast(null);
      }

      if (financialResult.status === 'fulfilled') {
        setFinancialSummary(financialResult.value.data.data);
      } else {
        console.error('Error loading Financial Summary:', financialResult.reason);
        setFinancialSummary(null);
      }

      if (partnerResult.status === 'fulfilled') {
        setFinancialByPartner(partnerResult.value.data.data);
      } else {
        console.error('Error loading Partners:', partnerResult.reason);
        setFinancialByPartner(null);
      }

      if (categoryResult.status === 'fulfilled') {
        setFinancialByCategory(categoryResult.value.data.data);
      } else {
        console.error('Error loading Categories:', categoryResult.reason);
        setFinancialByCategory(null);
      }

      if (monthlyResult.status === 'fulfilled') {
        setMonthlyInvoicing(monthlyResult.value.data.data);
      } else {
        console.error('Error loading Monthly Invoicing:', monthlyResult.reason);
        setMonthlyInvoicing(null);
      }
    } catch (error) {
      console.error('Error in loadDashboardData:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectionChange = (event) => {
    setSelectedConnection(event.target.value);
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  const handlePartnerClick = (data) => {
    setSelectedPartner(data);
  };

  const handleCategoryClick = (data) => {
    setSelectedCategory(data);
  };

  const handleMonthClick = async (data, type) => {
    console.log('handleMonthClick called with:', { data, type, selectedConnection, selectedYear });
    
    setSelectedMonth(data);
    setSelectedMonthType(type);
    
    // Cargar los contratos para este mes y tipo
    try {
      const month = data.month_number;
      console.log('Fetching contracts for:', { connection: selectedConnection, month, type, year: selectedYear });
      
      if (!month) {
        console.error('month_number is undefined in data:', data);
        setMonthContracts(null);
        return;
      }
      
      const response = await getMonthlyInvoicingContracts(selectedConnection, month, type, selectedYear);
      console.log('Response:', response);
      setMonthContracts(response.data.data);
    } catch (error) {
      console.error('Error loading monthly contracts:', error);
      setMonthContracts(null);
    }
  };

  const handleValidateContracts = async () => {
    setValidationLoading(true);
    try {
      const response = await validateContracts(selectedConnection);
      setValidationResults(response.data.data);
      setValidationDialogOpen(true);
    } catch (error) {
      console.error('Error validating contracts:', error);
    } finally {
      setValidationLoading(false);
    }
  };

  const handleCloseValidationDialog = () => {
    setValidationDialogOpen(false);
    setSeverityFilter('all');
    setExpandedIssue(null);
  };

  const handleExportToExcel = () => {
    const exportData = {
      kpis: financialSummary,
      partners: financialByPartner?.partners,
      categories: financialByCategory?.categories,
      monthly: monthlyInvoicing?.monthly
    };
    exportToExcel(exportData, `Dashboard_${selectedConnectionData?.name || 'export'}_${selectedYear}.xlsx`);
  };

  const handleExportToPDF = async () => {
    await exportToPDF('dashboard-content', `Dashboard_${selectedConnectionData?.name || 'export'}_${selectedYear}.pdf`);
  };

  const filteredIssues = validationResults?.issues?.filter(issue => 
    severityFilter === 'all' || issue.severity === severityFilter
  ) || [];

  const selectedConnectionData = connections.find(conn => conn.id === selectedConnection);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        📊 Dashboard by Connection
      </Typography>

      {connections.length === 0 ? (
        <Typography color="textSecondary">No connections available</Typography>
      ) : (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <FormControl fullWidth>
                    <InputLabel>Select Connection</InputLabel>
                    <Select
                      value={selectedConnection}
                      onChange={handleConnectionChange}
                      label="Select Connection"
                    >
                      {connections.map((connection) => (
                        <MenuItem key={connection.id} value={connection.id}>
                          {connection.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Select Year</InputLabel>
                    <Select
                      value={selectedYear}
                      onChange={handleYearChange}
                      label="Select Year"
                    >
                      {[2027, 2026, 2025, 2024, 2023, 2022, 2021, 2020].map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              {selectedConnectionData && (
                <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', mt: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>URL:</strong> {selectedConnectionData.url}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Database:</strong> {selectedConnectionData.database}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Viewing:</strong> Year {selectedYear} (vs {selectedYear - 1})
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={validationLoading ? <CircularProgress size={20} /> : <ValidateIcon />}
                      onClick={handleValidateContracts}
                      disabled={validationLoading}
                      sx={{ color: '#FF9800', borderColor: '#FF9800', '&:hover': { borderColor: '#F57C00' } }}
                    >
                      {validationLoading ? 'Validating...' : 'Validate Contracts'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<GetAppIcon />}
                      onClick={handleExportToExcel}
                      sx={{ ml: 1, color: '#4CAF50', borderColor: '#4CAF50', '&:hover': { borderColor: '#388E3C' } }}
                    >
                      Excel
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FileDownloadIcon />}
                      onClick={handleExportToPDF}
                      sx={{ ml: 1, color: '#2196F3', borderColor: '#2196F3', '&:hover': { borderColor: '#1565C0' } }}
                    >
                      PDF
                    </Button>
                  </Box>
                </Paper>
              )}
            </CardContent>
          </Card>

          {loading ? (
            <>
              {/* Financial Summary Skeletons */}
              <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                <Skeleton variant="text" width="40%" />
              </Typography>
              <Grid container spacing={2} sx={{ mb: 4 }}>
                {[1, 2, 3, 4].map((i) => (
                  <Grid item xs={12} sm={6} md={3} key={i}>
                    <KPISkeleton />
                  </Grid>
                ))}
              </Grid>

              {/* Chart Skeletons */}
              <ChartSkeleton height={Math.max(200, 10 * 15)} />
              
              <Box sx={{ mt: 4 }}>
                <ChartSkeleton height={300} />
              </Box>

              <Grid container spacing={2} sx={{ mt: 4 }}>
                <Grid item xs={12} md={6}>
                  <ChartSkeleton height={300} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ChartSkeleton height={300} />
                </Grid>
              </Grid>

              <Box sx={{ mt: 4 }}>
                <ChartSkeleton height={400} />
              </Box>
            </>
          ) : kpis || financialSummary ? (
            <>
              <Box id="dashboard-content" sx={{ width: '100%' }}>
              {/* FINANCIAL SUMMARY - Ingresos y Gastos */}
              {financialSummary && (
                <>
                  <Typography variant="h6" sx={{ mt: 4, mb: 2, color: '#9C27B0', fontWeight: 'bold' }}>
                    💰 Financial Summary - Year {selectedYear}
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <KPICard
                        icon={TrendingUpIcon}
                        label="Annual Revenue"
                        value={financialSummary.revenue}
                        unit="€"
                        bgColor="#4CAF50"
                        trend={financialSummary.revenue_trend}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <KPICard
                        icon={TrendingDownIcon}
                        label="Annual Expenses"
                        value={financialSummary.expenses}
                        unit="€"
                        bgColor="#FF5252"
                        trend={financialSummary.expenses_trend}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <KPICard
                        icon={MoneyIcon}
                        label="Net Profit"
                        value={financialSummary.profit}
                        unit="€"
                        bgColor="#9C27B0"
                        trend={financialSummary.profit_trend}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#E1BEE720', borderLeft: '4px solid #9C27B0' }}>
                        <Typography variant="body2" color="textSecondary">Invoices Processed</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                          {financialSummary.revenue_count} Revenue
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#FF5252' }}>
                          {financialSummary.expenses_count} Expenses
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </>
              )}

              {/* CHARTS */}
              {(forecast || financialByPartner) && (
                <>
                  {/* Filter partners with revenue > 0 */}
                  {(() => {
                    const partnersWithRevenue = financialByPartner?.partners?.filter(p => p.revenue > 0) || [];
                    return partnersWithRevenue.length > 0 && (
                      <Card sx={{ mt: 4 }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ mb: 2, color: '#2196F3', fontWeight: 'bold' }}>
                            💰 Revenue by Company ({partnersWithRevenue.length})
                          </Typography>
                          <ResponsiveContainer width="100%" height={Math.max(200, partnersWithRevenue.length * 15)}>
                            <BarChart
                              data={partnersWithRevenue}
                              margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis 
                                dataKey="partner_name" 
                                angle={-45}
                                textAnchor="end"
                                height={120}
                                interval={0}
                                tick={{ fontSize: 12 }}
                              />
                              <YAxis />
                              <Tooltip 
                                formatter={(value) => `€${value.toFixed(2)}`}
                                labelFormatter={(label) => `Company: ${label}`}
                              />
                              <Legend />
                              <Bar 
                                dataKey="revenue" 
                                fill="#2196F3" 
                                name="Revenue"
                                onClick={(data) => handlePartnerClick(data)}
                                style={{ cursor: 'pointer' }}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>
                    );
                  })()}

                  {/* Partner Details - Contracts List */}
                  {selectedPartner && (
                    <Card sx={{ mt: 4 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle1" sx={{ color: '#2196F3', fontWeight: 'bold' }}>
                            📋 Contracts for {selectedPartner.partner_name}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            onClick={() => setSelectedPartner(null)}
                            sx={{ cursor: 'pointer', color: '#FF6B6B', fontWeight: 'bold' }}
                          >
                            ✕ Close
                          </Typography>
                        </Box>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Paper sx={{ p: 2, bgcolor: '#E3F2FD' }}>
                              <Typography variant="body2" color="textSecondary">Total Revenue</Typography>
                              <Typography variant="h6" sx={{ color: '#2196F3', fontWeight: 'bold' }}>
                                €{selectedPartner.revenue.toFixed(2)}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Paper sx={{ p: 2, bgcolor: '#FFF3E0' }}>
                              <Typography variant="body2" color="textSecondary">Total Contracts</Typography>
                              <Typography variant="h6" sx={{ color: '#FF9800', fontWeight: 'bold' }}>
                                {selectedPartner.contracts ? selectedPartner.contracts.length : 0}
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>

                        <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}>
                          Contract Lines:
                        </Typography>
                        <Box sx={{ 
                          maxHeight: '400px', 
                          overflowY: 'auto',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          p: 2
                        }}>
                          {selectedPartner.contracts && selectedPartner.contracts.length > 0 ? (
                            selectedPartner.contracts.map((contract, index) => (
                              <Paper 
                                key={index} 
                                sx={{ 
                                  p: 2, 
                                  mb: 1.5,
                                  bgcolor: index % 2 === 0 ? '#f9f9f9' : '#fff',
                                  border: '1px solid #e0e0e0'
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'flex-start' }}>
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                      {contract.contract_name}
                                    </Typography>
                                    {contract.line_description && (
                                      <Typography variant="body2" sx={{ mb: 0.5, color: '#666' }}>
                                        {contract.line_description}
                                      </Typography>
                                    )}
                                    <Typography variant="caption" color="textSecondary">
                                      Contract ID: {contract.contract_id} | Line ID: {contract.line_id}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" sx={{ color: '#2196F3', fontWeight: 'bold' }}>
                                    €{contract.value.toFixed(2)}
                                  </Typography>
                                </Box>
                                
                                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                                  <Grid container spacing={1}>
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="caption" color="textSecondary" display="block">
                                        Unit Price
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        €{contract.price?.toFixed(2) || '0.00'}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="caption" color="textSecondary" display="block">
                                        Recurrence
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        {contract.recurring_type === 'monthly' ? 'Monthly' : 'Yearly'} (Interval: {contract.recurring_interval})
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="caption" color="textSecondary" display="block">
                                        Start Date
                                      </Typography>
                                      <Typography variant="body2">
                                        {contract.date_start || 'N/A'}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="caption" color="textSecondary" display="block">
                                        End Date
                                      </Typography>
                                      <Typography variant="body2">
                                        {contract.date_end || 'N/A'}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                  
                                  <Box sx={{ mt: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: '4px' }}>
                                    <Typography variant="caption" color="textSecondary" display="block">
                                      Annual Calculation
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#666' }}>
                                      {contract.calculation_formula}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Paper>
                            ))
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              No contracts found
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  )}

                  {/* Expenses by Partner Bar Chart */}
                  {(() => {
                    const partnersWithExpenses = financialByPartner?.partners?.filter(p => p.expenses > 0) || [];
                    return partnersWithExpenses.length > 0 && (
                      <Card sx={{ mt: 4 }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ mb: 2, color: '#FF5252', fontWeight: 'bold' }}>
                            💸 Expenses by Company ({partnersWithExpenses.length})
                          </Typography>
                          <ResponsiveContainer width="100%" height={Math.max(400, partnersWithExpenses.length * 35)}>
                            <BarChart
                              data={partnersWithExpenses}
                              margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis 
                                dataKey="partner_name" 
                                angle={-45}
                                textAnchor="end"
                                height={120}
                                interval={0}
                                tick={{ fontSize: 12 }}
                              />
                              <YAxis />
                              <Tooltip 
                                formatter={(value) => `€${value.toFixed(2)}`}
                                labelFormatter={(label) => `Company: ${label}`}
                              />
                              <Legend />
                              <Bar 
                                dataKey="expenses" 
                                fill="#FF5252" 
                                name="Expenses"
                                onClick={(data) => handlePartnerClick(data)}
                                style={{ cursor: 'pointer' }}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>
                    );
                  })()}

                  {/* Revenue by Product Category Bar Chart */}
                  {financialByCategory && financialByCategory.categories && financialByCategory.categories.length > 0 && (
                    <Card sx={{ mt: 4 }}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ mb: 2, color: '#4CAF50', fontWeight: 'bold' }}>
                          🏷️ Revenue by Product Category (All {financialByCategory.total_categories})
                        </Typography>
                        <ResponsiveContainer width="100%" height={Math.max(400, financialByCategory.categories.length * 30)}>
                          <BarChart
                            data={financialByCategory.categories}
                            margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="category_name" 
                              angle={-45}
                              textAnchor="end"
                              height={120}
                              interval={0}
                              tick={{ fontSize: 12 }}
                            />
                            <YAxis />
                            <Tooltip 
                              formatter={(value) => `€${value.toFixed(2)}`}
                              labelFormatter={(label) => `Category: ${label}`}
                            />
                            <Legend />
                            <Bar 
                              dataKey="revenue" 
                              fill="#4CAF50" 
                              name="Revenue"
                              onClick={(data) => handleCategoryClick(data)}
                              style={{ cursor: 'pointer' }}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                  {/* Category Details - Contracts List */}
                  {selectedCategory && (
                    <Card sx={{ mt: 4 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle1" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                            📋 Contracts for Category: {selectedCategory.category_name}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            onClick={() => setSelectedCategory(null)}
                            sx={{ cursor: 'pointer', color: '#FF6B6B', fontWeight: 'bold' }}
                          >
                            ✕ Close
                          </Typography>
                        </Box>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Paper sx={{ p: 2, bgcolor: '#E8F5E9' }}>
                              <Typography variant="body2" color="textSecondary">Total Revenue</Typography>
                              <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                                €{selectedCategory.revenue.toFixed(2)}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Paper sx={{ p: 2, bgcolor: '#FFF3E0' }}>
                              <Typography variant="body2" color="textSecondary">Total Contracts</Typography>
                              <Typography variant="h6" sx={{ color: '#FF9800', fontWeight: 'bold' }}>
                                {selectedCategory.contracts ? selectedCategory.contracts.length : 0}
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>

                        <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}>
                          Contract Lines:
                        </Typography>
                        <Box sx={{ 
                          maxHeight: '400px', 
                          overflowY: 'auto',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          p: 2
                        }}>
                          {selectedCategory.contracts && selectedCategory.contracts.length > 0 ? (
                            selectedCategory.contracts.map((contract, index) => (
                              <Paper 
                                key={index} 
                                sx={{ 
                                  p: 2, 
                                  mb: 1.5,
                                  bgcolor: index % 2 === 0 ? '#f9f9f9' : '#fff',
                                  border: '1px solid #e0e0e0'
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'flex-start' }}>
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                      {contract.contract_name}
                                    </Typography>
                                    {contract.partner_name && (
                                      <Typography variant="body2" sx={{ mb: 0.5, color: contract.is_customer ? '#2196F3' : '#FF6B6B', fontWeight: 'bold' }}>
                                        {contract.is_customer ? '👥 ' : '🏭 '}{contract.partner_name}
                                      </Typography>
                                    )}
                                    {contract.line_description && (
                                      <Typography variant="body2" sx={{ mb: 0.5, color: '#666' }}>
                                        {contract.line_description}
                                      </Typography>
                                    )}
                                    <Typography variant="caption" color="textSecondary">
                                      Contract ID: {contract.contract_id} | Line ID: {contract.line_id}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                                    €{contract.value.toFixed(2)}
                                  </Typography>
                                </Box>
                                
                                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                                  <Grid container spacing={1}>
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="caption" color="textSecondary" display="block">
                                        Unit Price
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        €{contract.price?.toFixed(2) || '0.00'}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="caption" color="textSecondary" display="block">
                                        Recurrence
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        {contract.recurring_rule_type === 'monthly' ? 'Monthly' : 
                                         contract.recurring_rule_type === 'yearly' ? 'Yearly' : 
                                         contract.recurring_rule_type || 'N/A'}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Paper>
                            ))
                          ) : (
                            <Typography variant="body2" color="textSecondary" align="center">
                              No contracts available for this category
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  )}

                  {/* Monthly Invoicing */}
                  {monthlyInvoicing && monthlyInvoicing.monthly && (
                    <Card sx={{ mt: 4 }}>
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ mb: 2, color: '#9C27B0', fontWeight: 'bold' }}>
                          📅 Monthly Invoicing Projection - {selectedYear}
                        </Typography>
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart data={monthlyInvoicing.monthly}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip formatter={(value) => `€${value.toFixed(2)}`} />
                            <Legend />
                            <Bar 
                              dataKey="revenue" 
                              fill="#2196F3" 
                              name="Customer Revenue"
                              onClick={(data) => handleMonthClick(data, 'customer')}
                              style={{ cursor: 'pointer' }}
                            />
                            <Bar 
                              dataKey="expenses" 
                              fill="#FF6B6B" 
                              name="Supplier Expenses"
                              onClick={(data) => handleMonthClick(data, 'supplier')}
                              style={{ cursor: 'pointer' }}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                  {/* Month Details - Contracts List */}
                  {selectedMonth && monthContracts && (
                    <Card sx={{ mt: 4 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle1" sx={{ color: selectedMonthType === 'customer' ? '#2196F3' : '#FF6B6B', fontWeight: 'bold' }}>
                            📋 {selectedMonthType === 'customer' ? '👥 Customer' : '🏭 Supplier'} Contracts for {selectedMonth.month}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            onClick={() => setSelectedMonth(null)}
                            sx={{ cursor: 'pointer', color: '#FF6B6B', fontWeight: 'bold' }}
                          >
                            ✕ Close
                          </Typography>
                        </Box>
                        
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={12} sm={6}>
                            <Paper sx={{ p: 2, bgcolor: selectedMonthType === 'customer' ? '#E3F2FD' : '#FCE4EC' }}>
                              <Typography variant="body2" color="textSecondary">Total {selectedMonthType === 'customer' ? 'Revenue' : 'Expenses'}</Typography>
                              <Typography variant="h6" sx={{ color: selectedMonthType === 'customer' ? '#2196F3' : '#FF6B6B', fontWeight: 'bold' }}>
                                €{monthContracts.total_value.toFixed(2)}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Paper sx={{ p: 2, bgcolor: '#F5F5F5' }}>
                              <Typography variant="body2" color="textSecondary">Total Contracts</Typography>
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {monthContracts.total}
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>

                        <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}>
                          Contracts ({monthContracts.total}):
                        </Typography>
                        <Box sx={{ 
                          maxHeight: '600px', 
                          overflowY: 'auto',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          p: 2
                        }}>
                          {monthContracts.contracts && monthContracts.contracts.length > 0 ? (
                            monthContracts.contracts.map((contract, index) => (
                              <Paper 
                                key={index} 
                                sx={{ 
                                  p: 2, 
                                  mb: 1.5,
                                  bgcolor: index % 2 === 0 ? '#f9f9f9' : '#fff',
                                  border: '1px solid #e0e0e0',
                                  borderLeft: `4px solid ${selectedMonthType === 'customer' ? '#2196F3' : '#FF6B6B'}`
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'flex-start' }}>
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                      {contract.contract_name}
                                    </Typography>
                                    {contract.partner_name && (
                                      <Typography variant="body2" sx={{ color: selectedMonthType === 'customer' ? '#2196F3' : '#FF6B6B', fontWeight: 'bold' }}>
                                        {contract.partner_name}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Typography variant="body2" sx={{ color: selectedMonthType === 'customer' ? '#2196F3' : '#FF6B6B', fontWeight: 'bold' }}>
                                    €{contract.price.toFixed(2)}
                                  </Typography>
                                </Box>
                                
                                {contract.lines && contract.lines.length > 0 && (
                                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666', display: 'block', mb: 1 }}>
                                      Items ({contract.lines.length}):
                                    </Typography>
                                    {contract.lines.map((line, lineIndex) => (
                                      <Box 
                                        key={lineIndex}
                                        sx={{ 
                                          p: 1, 
                                          mb: 1, 
                                          bgcolor: '#fafafa', 
                                          borderRadius: '3px',
                                          borderLeft: '2px solid #ccc'
                                        }}
                                      >
                                        <Typography variant="body2" sx={{ fontWeight: '500', mb: 0.5 }}>
                                          {line.line_name}
                                        </Typography>
                                        {line.product_name && (
                                          <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                                            {line.product_name}
                                          </Typography>
                                        )}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <Typography variant="caption" color="textSecondary">
                                            {line.recurring_type === 'monthly' ? 'Monthly' : 
                                             line.recurring_type === 'yearly' ? 'Yearly' : 
                                             line.recurring_type || 'N/A'} {line.recurring_interval > 1 ? `(every ${line.recurring_interval})` : ''}
                                          </Typography>
                                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                                            €{line.price.toFixed(2)}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                              </Paper>
                            ))
                          ) : (
                            <Typography variant="body2" color="textSecondary" align="center">
                              No contracts available for this month
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              {/* VALIDATION DIALOG */}
              <Dialog 
                open={validationDialogOpen} 
                onClose={handleCloseValidationDialog}
                maxWidth="md"
                fullWidth
              >
                <DialogTitle sx={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ValidateIcon sx={{ color: '#FF9800' }} />
                  Contract Validation Report
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                  {validationResults && (
                    <Box>
                      {/* VALIDATION RULES SECTION */}
                      <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>
                        <Box 
                          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                          onClick={() => setShowValidationRules(!showValidationRules)}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            📋 Validation Rules
                          </Typography>
                          {showValidationRules ? <ExpandLess /> : <ExpandMore />}
                        </Box>
                        <Collapse in={showValidationRules} timeout="auto" unmountOnExit>
                          <Box sx={{ mt: 2, fontSize: '0.85rem' }}>
                            <Typography variant="caption" display="block" sx={{ mb: 1, fontWeight: 'bold', color: '#d32f2f' }}>
                              🔴 HIGH SEVERITY:
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ ml: 2, mb: 1 }}>
                              • Date Inconsistency: end_date &lt; start_date
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ ml: 2, mb: 1 }}>
                              • Negative Price: price &lt; 0
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ ml: 2, mb: 1 }}>
                              • Invalid Interval: recurring_interval = 0
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ ml: 2, mb: 2 }}>
                              • Title with Year Mismatch: title contains year but recurring_type ≠ 'yearly'
                            </Typography>

                            <Typography variant="caption" display="block" sx={{ mb: 1, fontWeight: 'bold', color: '#f57c00' }}>
                              🟠 MEDIUM SEVERITY:
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ ml: 2, mb: 1 }}>
                              • Zero Price: price = 0 AND recurring_type is set
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ ml: 2, mb: 1 }}>
                              • Recurrence Mismatch (monthly): type='monthly' AND interval≥12
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ ml: 2, mb: 2 }}>
                              • Contract State Mismatch: line in cancelled/terminated contract
                            </Typography>

                            <Typography variant="caption" display="block" sx={{ mb: 1, fontWeight: 'bold', color: '#1976d2' }}>
                              🔵 LOW SEVERITY:
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ ml: 2, mb: 1 }}>
                              • Recurrence Mismatch (yearly): type='yearly' AND interval&gt;1
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ ml: 2 }}>
                              • Partner Consistency: price&gt;0 but customer_rank=0 AND supplier_rank=0
                            </Typography>
                          </Box>
                        </Collapse>
                      </Paper>

                      {/* SUMMARY CARDS */}
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6}>
                          <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              Total Contract Lines
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                              {validationResults.total_lines}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              Total Issues
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: validationResults.total_issues > 0 ? '#FF9800' : '#4CAF50' }}>
                              {validationResults.total_issues}
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>

                      {/* SEVERITY SUMMARY */}
                      <Grid container spacing={1} sx={{ mb: 3 }}>
                        <Grid item xs={6} sm={4}>
                          <Chip
                            icon={<ErrorIcon />}
                            label={`High: ${validationResults.summary.high_severity}`}
                            color={validationResults.summary.high_severity > 0 ? 'error' : 'default'}
                            variant="outlined"
                            sx={{ width: '100%' }}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Chip
                            icon={<WarningIcon />}
                            label={`Medium: ${validationResults.summary.medium_severity}`}
                            color={validationResults.summary.medium_severity > 0 ? 'warning' : 'default'}
                            variant="outlined"
                            sx={{ width: '100%' }}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Chip
                            icon={<InfoIcon />}
                            label={`Low: ${validationResults.summary.low_severity}`}
                            color={validationResults.summary.low_severity > 0 ? 'info' : 'default'}
                            variant="outlined"
                            sx={{ width: '100%' }}
                          />
                        </Grid>
                      </Grid>

                      {validationResults.total_issues === 0 ? (
                        <Alert severity="success">✓ No issues detected. All contract data looks good!</Alert>
                      ) : (
                        <>
                          {/* FILTER */}
                          <TextField
                            select
                            size="small"
                            label="Filter by Severity"
                            value={severityFilter}
                            onChange={(e) => setSeverityFilter(e.target.value)}
                            sx={{ mb: 2, width: '200px' }}
                          >
                            <MenuItem value="all">All Severities</MenuItem>
                            <MenuItem value="high">High Only</MenuItem>
                            <MenuItem value="medium">Medium Only</MenuItem>
                            <MenuItem value="low">Low Only</MenuItem>
                          </TextField>

                          {/* ISSUES LIST */}
                          <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>
                            Issues ({filteredIssues.length})
                          </Typography>
                          <List sx={{ border: '1px solid #ddd', borderRadius: 1, maxHeight: '400px', overflowY: 'auto' }}>
                            {filteredIssues.map((issue) => (
                              <React.Fragment key={`${issue.line_id}-${issue.type}`}>
                                <ListItem
                                  button
                                  onClick={() => setExpandedIssue(expandedIssue === issue.line_id ? null : issue.line_id)}
                                  sx={{
                                    backgroundColor: issue.severity === 'high' ? '#ffebee' : issue.severity === 'medium' ? '#fff3e0' : '#f0f4ff',
                                    borderBottom: '1px solid #eee',
                                    '&:hover': { backgroundColor: issue.severity === 'high' ? '#ffcdd2' : issue.severity === 'medium' ? '#ffe0b2' : '#e3f2fd' }
                                  }}
                                >
                                  <ListItemIcon>
                                    {issue.severity === 'high' && <ErrorIcon sx={{ color: '#d32f2f' }} />}
                                    {issue.severity === 'medium' && <WarningIcon sx={{ color: '#f57c00' }} />}
                                    {issue.severity === 'low' && <InfoIcon sx={{ color: '#1976d2' }} />}
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={`Line ${issue.line_id} - ${issue.type.replace(/_/g, ' ')}`}
                                    secondary={`Contract: ${issue.contract_id} | ${issue.description.substring(0, 50)}...`}
                                    primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                                  />
                                  {expandedIssue === issue.line_id ? <ExpandLess /> : <ExpandMore />}
                                </ListItem>
                                <Collapse in={expandedIssue === issue.line_id} timeout="auto" unmountOnExit>
                                  <Box sx={{ pl: 4, pr: 2, py: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                                    <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                      <strong>Type:</strong> {issue.type}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                      <strong>Line ID:</strong> {issue.line_id}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                      <strong>Contract ID:</strong> {issue.contract_id}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                      <strong>Current Value:</strong> {JSON.stringify(issue.current_value)}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                      <strong>Description:</strong> {issue.description}
                                    </Typography>
                                    {issue.suggestion && (
                                      <Typography variant="caption" display="block" sx={{ mt: 1, color: '#1976d2', fontStyle: 'italic' }}>
                                        💡 {issue.suggestion}
                                      </Typography>
                                    )}
                                  </Box>
                                </Collapse>
                              </React.Fragment>
                            ))}
                          </List>
                        </>
                      )}
                    </Box>
                  )}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #ddd' }}>
                  <Button onClick={handleCloseValidationDialog} variant="contained">
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
              </Box>
            </>
          ) : null}
        </>
      )}
    </Container>
  );
}

export default DashboardByConnection;
